import { useCallback, useRef, useState } from 'react';
import { QBTestQuestionEto } from '../lib/api/types';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { getObjectName } from '../utils/fabricHelpers';
import { fabric, FabricImage, Rect, Shadow, Ellipse, FabricText, Group } from '../lib/fabricLoader';
import type { Canvas as CanvasType } from 'fabric';
import { removeChoiceStrokes, removeQuestionStrokes } from '../utils/opticalChoiceOverlay';
import { enforceZOrder, placeObjectInCorrectLayer } from '../utils/canvasZOrder';
import { calculateQuestionPosition } from '../utils/questionPositioning';

interface QuestionMetadata {
  scale: number;
  left: number;
  top: number;
}

interface UseQuestionBackgroundProps {
  fabricCanvasRef: React.MutableRefObject<CanvasType | null>;
  isOpticalMode: boolean;
  isMobile: boolean;
  showAnswerFeedback: boolean;
  opticalAnswers: Record<string, string>;
  eliminatedChoices: Record<string, string>;
  onOpticalChoiceClick?: (choice: string, questionId: string) => void;
}

export const useQuestionBackground = ({
  fabricCanvasRef,
  isOpticalMode,
  isMobile,
  showAnswerFeedback,
  opticalAnswers,
  eliminatedChoices,
  onOpticalChoiceClick
}: UseQuestionBackgroundProps) => {
  // Get device preferences for question frame margins
  const { devicePreferences } = useWhiteboardStore();
  const [questionMetadata, setQuestionMetadata] = useState<Record<string, QuestionMetadata>>({});
  const isLoadingQuestionRef = useRef(false);
  const orientationChangedRef = useRef(false);
  const previousOrientationRef = useRef<boolean | null>(null);
  const currentLoadingQuestionIdRef = useRef<string | null>(null);
  const isFirstQuestionLoadRef = useRef(true);
  const canvasDimensionsStableRef = useRef(false);

  const loadQuestionBackground = useCallback(async (question: QBTestQuestionEto, isRetry: boolean = false) => {
   
    
    const canvas = fabricCanvasRef.current;
    
    // Load question background
    
    if (!canvas || !question.imageUrl) {
      console.warn('Missing canvas or imageUrl:', { canvas: !!canvas, imageUrl: question.imageUrl });
      return;
    }

    // For mobile WebView first question, wait for canvas dimensions to stabilize
    const isWebView = window.location.pathname.includes('webview') || window.location.pathname.includes('WebView');
    const isMobileDevice = window.innerWidth < 768;
    
    if (isFirstQuestionLoadRef.current && isWebView && isMobileDevice) {
      console.log('First question load in mobile WebView - checking canvas stability');
      
      // Wait for canvas dimensions to stabilize
      let stabilityCheckCount = 0;
      let lastWidth = 0;
      let lastHeight = 0;
      
      const checkCanvasStability = async () => {
        return new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            const currentWidth = canvas.getWidth();
            const currentHeight = canvas.getHeight();
            
            // Check if dimensions are stable (not changing)
            if (currentWidth === lastWidth && currentHeight === lastHeight && currentWidth > 0 && currentHeight > 0) {
              stabilityCheckCount++;
              
              // Consider stable after 3 consecutive checks with same dimensions
              if (stabilityCheckCount >= 3) {
                clearInterval(checkInterval);
                canvasDimensionsStableRef.current = true;
                console.log('Canvas dimensions stabilized:', { width: currentWidth, height: currentHeight });
                resolve();
              }
            } else {
              // Reset counter if dimensions changed
              stabilityCheckCount = 0;
              lastWidth = currentWidth;
              lastHeight = currentHeight;
            }
            
            // Timeout after 1 second
            if (stabilityCheckCount * 50 > 1000) {
              clearInterval(checkInterval);
              console.log('Canvas stability check timeout - proceeding');
              resolve();
            }
          }, 50); // Check every 50ms
        });
      };
      
      await checkCanvasStability();
      
      // Additional delay for WebView to fully settle
      await new Promise(resolve => setTimeout(resolve, 200));
      
      isFirstQuestionLoadRef.current = false;
    }
    
    // Store initial canvas dimensions for comparison later
    const initialCanvasWidth = canvas.getWidth();
    const initialCanvasHeight = canvas.getHeight();

    // Prevent concurrent loads
    if (isLoadingQuestionRef.current && currentLoadingQuestionIdRef.current === question.questionId) {
    
      return;
    }
    
    // If loading a different question, wait for previous to complete
    if (isLoadingQuestionRef.current && currentLoadingQuestionIdRef.current !== question.questionId) {
     
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    isLoadingQuestionRef.current = true;
    currentLoadingQuestionIdRef.current = question.questionId || null;

    try {
      // Load parent image if exists
      let parentImg: FabricImage | null = null;
      let parentImageHeight = 0;
      
      if (question.parentQuestionImageUrl) {
        try {
          const parentImgUrl = `${question.parentQuestionImageUrl}${question.parentQuestionImageUrl?.includes('?') ? '&' : '?'}t=${Date.now()}`;
          
          // Use same loading method as main image
          const parentImgElement = new Image();
          // Set crossOrigin before setting src to ensure CORS headers are sent
          parentImgElement.crossOrigin = 'anonymous';
          
          await new Promise<void>((resolve, reject) => {
            parentImgElement.onload = () => resolve();
            parentImgElement.onerror = (err) => reject(err);
            parentImgElement.src = parentImgUrl;
          });
          
          parentImg = new FabricImage(parentImgElement);
          
          // Disable caching to prevent render issues
          parentImg.objectCaching = false;
          
          // Force clear any cached canvas to prevent zero-dimension errors
          (parentImg as any)._cacheCanvas = null;
          (parentImg as any)._cacheContext = null;
          (parentImg as any).cacheWidth = 0;
          (parentImg as any).cacheHeight = 0;
          parentImg.dirty = true;
          
          if (parentImg && parentImg.width && parentImg.height) {
            parentImageHeight = parentImg.height || 0;
            (parentImg as any).questionId = question.questionId;
            (parentImg as any).timestamp = Date.now();
          }
        } catch (error) {
          console.error('Failed to load parent question image:', error);
        }
      }

      // Remove ONLY question-related elements, NOT user drawings
      const allObjects = [...canvas.getObjects()]; // Create a copy to avoid modification during iteration
      const questionElements = allObjects.filter(obj => {
        const name = getObjectName(obj);
        const objQuestionId = (obj as any).data?.questionId || (obj as any).questionId;
        
        // Skip user-drawn strokes (paths and other drawing objects)
        // Check for strokeId which indicates user-drawn content
        if ((obj as any).strokeId || (obj as any).isUserDrawing) {
          return false; // Don't remove user drawings
        }
        
        // Skip paths without specific names (these are user drawings)
        if (obj.type === 'path' && !name) {
          return false; // Don't remove user-drawn paths
        }
        
        // Remove elements that belong to this specific question OR are general question elements
        const isCurrentQuestionElement = objQuestionId === question.questionId;
        
        // Also check for any image objects that might be question backgrounds
        const isQuestionImage = obj.type === 'image' && (
          name === 'questionBackground' || 
          name === 'parentQuestionBackground' ||
          (!name && obj.type === 'image' && (obj as any).width > 500) // Large unnamed images might be old question backgrounds
        );
        
        const isQuestionRelated = isQuestionImage ||
               name === 'questionFrame' ||
               name === 'questionResizeHandle' ||
               name === 'questionMoveHandle' ||
               name === 'questionMoveIcon' ||
               name?.startsWith('opticalChoice_') ||
               name?.startsWith('opticalChoiceFeedback_') ||
               name?.startsWith('opticalQuestionNumber_') ||
               name?.startsWith('questionOpticalChoice_') ||
               name?.startsWith('opticalSelectionCircle_');
               
        return isQuestionRelated || isCurrentQuestionElement;
      });
      
   
      
      // Remove each element
      questionElements.forEach(obj => {
        canvas.remove(obj);
      });
      
      // Force a render to ensure cleanup is visible
      canvas.requestRenderAll();
      
      // Small delay to ensure cleanup completes
      await new Promise(resolve => setTimeout(resolve, 50));

      // Load main question image
      const imgUrl = `${question.imageUrl}${question.imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
      // Load image from URL
      
      // Load image using Promise pattern (Fabric.js 6.0)
      let img: FabricImage | null = null;
      
      try {
        // Try alternative loading method for better CORS handling
        const imgElement = new Image();
        // Set crossOrigin before setting src to ensure CORS headers are sent
        imgElement.crossOrigin = 'anonymous';
        
        // Create a promise for image loading
        await new Promise<void>((resolve, reject) => {
          imgElement.onload = () => resolve();
          imgElement.onerror = (err) => reject(err);
          imgElement.src = imgUrl;
        });
        
        // Create FabricImage from loaded element
        img = new FabricImage(imgElement);
        
        // Disable caching to prevent render issues
        img.objectCaching = false;
        
        // Force clear any cached canvas to prevent zero-dimension errors
        (img as any)._cacheCanvas = null;
        (img as any)._cacheContext = null;
        (img as any).cacheWidth = 0;
        (img as any).cacheHeight = 0;
        img.dirty = true;
        
        // Image loaded successfully
      } catch (error) {
        console.error('Error loading image:', error);
        
        // Fallback: Try without crossOrigin
        try {
        
          img = await FabricImage.fromURL(imgUrl);
          
          if (img) {
            // Disable caching to prevent render issues
            img.objectCaching = false;
           
          }
        } catch (fallbackError) {
          console.error('Fallback loading also failed:', fallbackError);
          return;
        }
      }
      
      // Verify image loaded
      
      if (!img || !img.width || !img.height) {
        console.error('Image failed to load properly', {
          img: !!img,
          width: img?.width,
          height: img?.height,
          url: imgUrl
        });
        return;
      }
      
    

      // Calculate positioning - use actual canvas dimensions, not window dimensions
      // This ensures we get the correct dimensions after WebView has stabilized
      const canvasWidth = canvas.getWidth() || canvas.width || window.innerWidth;
      const canvasHeight = canvas.getHeight() || canvas.height || window.innerHeight;
      const imgWidth = img.width || 1;
      const imgHeight = img.height || 1;
      
      // Calculate dimensions
      
      // Check if orientation has changed
      const currentOrientation = window.innerWidth > window.innerHeight;
      orientationChangedRef.current = previousOrientationRef.current !== null && 
                                    previousOrientationRef.current !== currentOrientation;
      previousOrientationRef.current = currentOrientation;
      
      // Force position recalculation on orientation change
      if (orientationChangedRef.current && questionMetadata[question.questionId || '']) {
        // Clear saved metadata on orientation change to force recalculation
        delete questionMetadata[question.questionId || ''];
      }
      
      // Get user margins based on device type and orientation
      let userMargins: { top?: number; bottom?: number; left?: number; right?: number } | undefined;
      
      if (devicePreferences?.preferences?.questionFrameMargins) {
        const frameMargins = devicePreferences.preferences.questionFrameMargins;
        const isTablet = window.innerWidth >= 768;
        const isLandscape = window.innerWidth > window.innerHeight;
        
        // Get device-specific margins
        if (isMobile && !isTablet) {
          if (isLandscape && frameMargins.mobile?.landscape) {
            userMargins = frameMargins.mobile.landscape;
          } else if (!isLandscape && frameMargins.mobile?.portrait) {
            userMargins = frameMargins.mobile.portrait;
          }
        } else if (isTablet) {
          if (isLandscape && frameMargins.tablet?.landscape) {
            userMargins = frameMargins.tablet.landscape;
          } else if (!isLandscape && frameMargins.tablet?.portrait) {
            userMargins = frameMargins.tablet.portrait;
          }
        } else if (frameMargins.desktop) {
          userMargins = frameMargins.desktop;
        }
        
        // Fallback to global margins if device-specific not found
        if (!userMargins) {
          userMargins = {
            top: frameMargins.top,
            bottom: frameMargins.bottom,
            left: frameMargins.left,
            right: frameMargins.right
          };
        }
      }
      
      // Smart positioning logic
      const positioning = calculateQuestionPosition({
        canvasWidth,
        canvasHeight,
        imgWidth,
        imgHeight,
        parentImageHeight: parentImageHeight || 0,
        isOpticalMode,
        isMobile,
        isTablet: window.innerWidth >= 768,
        isLandscape: window.innerWidth > window.innerHeight,
        savedMetadata: questionMetadata[question.questionId || ''],
        parentImg: parentImg || undefined,
        userMargins
      });
      
      // Positioning calculated

      // Create frame and position images exactly like old implementation
      let parentImageBottom = positioning.top;
      let calculatedFrameHeight = imgHeight * positioning.scale;
      let questionImageTop = positioning.top;
      
      // Check if frame already exists to prevent duplicates
      const existingFrame = canvas.getObjects().find(obj => 
        getObjectName(obj) === 'questionFrame'
      );
      
      if (existingFrame) {
      
        canvas.remove(existingFrame);
      }
      
      // Create a subtle frame for the image (matching old implementation)
      const padding = 0; // No padding for frame
      const frameWidth = Math.max(imgWidth * positioning.scale, 1);
      const frameHeight = Math.max(calculatedFrameHeight, 1);
      
    
      
      // Get current tool for select mode
      const currentToolState = useWhiteboardStore.getState().currentTool;
      const isInSelectMode = currentToolState === 'select' && !isMobile;
      
      const frame = new Rect({
        name: 'questionFrame',
        left: positioning.left,
        top: positioning.top,
        width: frameWidth,
        height: frameHeight,  // Will be updated if parent exists
        fill: 'transparent', // Make frame transparent
        stroke: isInSelectMode ? '#3b82f6' : 'transparent', // Show border in select mode
        strokeWidth: isInSelectMode ? 2 : 0,
        strokeDashArray: isInSelectMode ? [5, 5] : undefined, // Dashed border in select mode
        opacity: isInSelectMode ? 0.3 : 0, // Slightly visible in select mode
        selectable: isInSelectMode, // Selectable in select mode
        evented: isInSelectMode,
        hasControls: isInSelectMode,
        hasBorders: isInSelectMode,
        lockRotation: true, // No rotation allowed
        lockScalingFlip: true,
        transparentCorners: false,
        cornerColor: '#3b82f6',
        cornerStrokeColor: '#1e40af',
        borderColor: '#3b82f6',
        cornerSize: 10,
        cornerStyle: 'rect',
        excludeFromExport: false,
        erasable: false as any,
        data: { 
          questionId: question.questionId,
          originalScale: positioning.scale,
          originalLeft: positioning.left,
          originalTop: positioning.top,
          hasParent: !!parentImg
        } // Add metadata for tracking
      });
      
      // Position parent image if exists (exactly like old implementation)
      if (parentImg && parentImageHeight > 0) {
        // Calculate parent image scale to match question width
        const parentImgScale = positioning.scale * (imgWidth / (parentImg.width || 1));
        const scaledParentHeight = parentImageHeight * parentImgScale;
        
        // Mobile-specific spacing adjustments
        let spacingBetweenImages = 20;
        if (isMobile) {
          spacingBetweenImages = window.innerWidth > window.innerHeight ? 10 : 15;
        }
        
        // Set parent image at base position (exactly like old implementation)
        parentImg.set({
          name: 'parentQuestionBackground',
          data: { 
            questionId: question.questionId,
            originalLeft: positioning.left,
            originalTop: positioning.top
          },
          left: positioning.left,
          top: positioning.top,  // Parent at base position
          scaleX: parentImgScale,
          scaleY: parentImgScale,
          selectable: false,  // Parent image is not directly selectable
          evented: false,
          lockMovementX: true,
          lockMovementY: true,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true,
          hasControls: false,
          hasBorders: false,
          opacity: 1,
          excludeFromExport: false,
          erasable: false
        });
        
        // Update positions for question image (exactly like old implementation)
        parentImageBottom = positioning.top + scaledParentHeight;
        questionImageTop = parentImageBottom + spacingBetweenImages;
        calculatedFrameHeight = scaledParentHeight + spacingBetweenImages + imgHeight * positioning.scale;
        
        // Update frame height to encompass both images
        frame.set('height', Math.max(calculatedFrameHeight, 1));
      }
      
      // Check if question image already exists
      const existingQuestionImg = canvas.getObjects().find(obj => 
        getObjectName(obj) === 'questionBackground' && 
        (obj as any).data?.questionId === question.questionId
      );
      
      if (existingQuestionImg) {
      
        canvas.remove(existingQuestionImg);
      }
      
      // Check if parent image already exists
      if (parentImg) {
        const existingParentImg = canvas.getObjects().find(obj => 
          getObjectName(obj) === 'parentQuestionBackground' && 
          (obj as any).data?.questionId === question.questionId
        );
        
        if (existingParentImg) {
        
          canvas.remove(existingParentImg);
        }
      }
      
      // Add frame first and send it to back immediately
      canvas.add(frame);
      canvas.sendObjectToBack(frame); // Ensure frame is at the back
      
      // Add parent image if exists
      if (parentImg) {      
        canvas.add(parentImg);
      }
      
      // Apply bounds checking to ensure image stays completely within canvas
      let finalScale = positioning.scale;
      let finalLeft = positioning.left;
      let finalTop = questionImageTop;
      
      // Get actual canvas bounds
      const canvasBounds = {
        width: canvas.getWidth(),
        height: canvas.getHeight()
      };
      
      // Calculate scaled dimensions
      const scaledWidth = imgWidth * positioning.scale;
      const scaledHeight = imgHeight * positioning.scale;
      
      // Check horizontal bounds
      if (finalLeft + scaledWidth > canvasBounds.width) {
        // Image would overflow right edge
        finalLeft = canvasBounds.width - scaledWidth;
      }
      
      // Check vertical bounds
      if (finalTop + scaledHeight > canvasBounds.height) {
        // Image would overflow bottom edge
        finalTop = canvasBounds.height - scaledHeight;
      }
      
      // Ensure left and top are not negative
      // For desktop, ensure minimum top to avoid exit button (25px height + 25px spacing)
      finalLeft = Math.max(0, finalLeft);
      finalTop = Math.max(35, finalTop);
      
      // If image still doesn't fit, scale it down
      if (scaledWidth > canvasBounds.width || scaledHeight > canvasBounds.height) {
        const scaleX = canvasBounds.width / imgWidth;
        const scaleY = canvasBounds.height / imgHeight;
        finalScale = Math.min(scaleX, scaleY, positioning.scale) * 0.95; // 5% safety margin
      }

      img.set({
        name: 'questionBackground',
        data: { 
          questionId: question.questionId,
          originalLeft: finalLeft,
          originalTop: finalTop
        },
        left: finalLeft,
        top: finalTop,  // Uses boundary-checked position
        scaleX: finalScale,
        scaleY: finalScale,
        originX: 'left',  // Explicitly set origin to ensure proper positioning
        originY: 'top',
        selectable: false,  // Not selectable - frame handles selection
        evented: false,     // No events - frame handles interaction
        lockMovementX: true,
        lockMovementY: true,
        lockRotation: true,
        lockScalingX: true,
        lockScalingY: true,
        lockScalingFlip: true,
        hasControls: false,
        hasBorders: false,
        opacity: 1,
        excludeFromExport: false,
        erasable: false
      });
      
      // Verify image dimensions before adding
    
      // Force image to update
      img.setCoords();
      canvas.add(img);
  
      
      // Only lock the image if NOT in select mode
      if (!isInSelectMode) {
        img.set({
          evented: false,
          erasable: false,
          excludeFromExport: false,
          hasControls: false,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          lockRotation: true,
          lockScalingX: true,
          lockScalingY: true
        });
      }
      
      // Temporarily disable auto-rendering while adding multiple objects
      const previousRenderOnAddRemove = canvas.renderOnAddRemove;
      canvas.renderOnAddRemove = false;
      
      // Double-check image element is valid after adding to canvas
      const imgElement = img.getElement();
      if (imgElement && imgElement instanceof HTMLImageElement && (!imgElement.complete || imgElement.naturalWidth === 0)) {
     
        imgElement.onload = () => {
       
          canvas.requestRenderAll();
        };
      }
      
      // Mark the question as loaded
      (img as any).isQuestionImage = true;
      (img as any).loadedAt = Date.now();
      
      // Image added to canvas

      // Add frame event handlers when in select mode
      if (isInSelectMode) {
        // Handle frame scaling
        frame.on('scaling', () => {
          const newScaleX = frame.scaleX || 1;
          const newScaleY = frame.scaleY || 1;
          
          // Keep uniform scaling
          const uniformScale = Math.min(newScaleX, newScaleY);
          frame.scaleX = uniformScale;
          frame.scaleY = uniformScale;
          
          // Calculate new scale for images based on original scale
          const originalScale = (frame as any).data?.originalScale || positioning.scale;
          const imageScale = originalScale * uniformScale;
          
          // Update question image scale
          img.set({
            scaleX: imageScale,
            scaleY: imageScale
          });
          
          // Update parent image if exists
          if (parentImg) {
            const parentScale = imageScale * (imgWidth / (parentImg.width || 1));
            parentImg.set({
              scaleX: parentScale,
              scaleY: parentScale
            });
          }
          
          // Update optical choices using frame-based positioning
          updateOpticalChoicePositionsFrameBased(canvas, frame, imageScale);
          
          canvas.requestRenderAll();
        });
        
        // Store last position for delta calculation
        let lastFrameLeft = frame.left || positioning.left;
        let lastFrameTop = frame.top || positioning.top;
        
        // Handle frame moving
        frame.on('moving', () => {
          // Boundary check for frame
          const bounds = frame.getBoundingRect();
          let newLeft = frame.left || 0;
          let newTop = frame.top || 0;
          
          // Horizontal bounds
          if (bounds.left < 0) newLeft = 0;
          if (bounds.left + bounds.width > canvas.width) {
            newLeft = canvas.width - bounds.width;
          }
          
          // Vertical bounds - minimum 35px from top
          if (bounds.top < 35) newTop = 35;
          if (bounds.top + bounds.height > canvas.height) {
            newTop = canvas.height - bounds.height;
          }
          
          // Apply constrained position to frame
          frame.set({ left: newLeft, top: newTop });
          
          // Calculate delta from last position
          const deltaX = newLeft - lastFrameLeft;
          const deltaY = newTop - lastFrameTop;
          
          // Update parent image position if exists
          if (parentImg) {
            parentImg.set({
              left: (parentImg.left || 0) + deltaX,
              top: (parentImg.top || 0) + deltaY
            });
          }
          
          // Update question image position
          img.set({
            left: (img.left || 0) + deltaX,
            top: (img.top || 0) + deltaY
          });
          
          // Update last position
          lastFrameLeft = newLeft;
          lastFrameTop = newTop;
          
          // Update optical choices using frame-based positioning
          updateOpticalChoicePositionsFrameBased(canvas, frame, img.scaleX || 1);
          
          canvas.requestRenderAll();
        });
        
        // Handle frame modification complete
        frame.on('modified', () => {
          // Save metadata based on frame position
          const frameScale = frame.scaleX || 1;
          const originalScale = (frame as any).data?.originalScale || positioning.scale;
          const imageScale = originalScale * frameScale;
          
          setQuestionMetadata(prev => ({
            ...prev,
            [question.questionId!]: {
              scale: imageScale,
              left: frame.left || 0,
              top: frame.top || 0
            }
          }));
        });
      }
      
      // Set proper z-order
      // 1. Frame at the very back
      canvas.sendObjectToBack(frame);
      
      // 2. Parent image above frame (if exists)
      if (parentImg) {
        canvas.sendObjectToBack(parentImg);
        canvas.moveObjectTo(parentImg, 1); // Just above frame
      }
      
      // 3. Question image above parent (or frame if no parent)
      canvas.sendObjectToBack(img);
      const baseIndex = 1 + (parentImg ? 1 : 0);
      canvas.moveObjectTo(img, baseIndex);
      
      // Use z-order utility to ensure all objects are in correct layers
      enforceZOrder(canvas);
      
      
      // // Add debug overlay in development
      // if (import.meta.env.DEV && isMobile) {
      //   // Remove existing debug overlay
      //   const existingDebug = canvas.getObjects().find(obj => (obj as any).name === 'debugOverlay');
      //   if (existingDebug) canvas.remove(existingDebug);

      //   // Create debug margin guides
      //   const debugGroup = new fabric.Group([
      //     // Left margin line
      //     new fabric.Line([appliedLeft, 0, appliedLeft, canvas.getHeight()], {
      //       stroke: 'red',
      //       strokeWidth: 1,
      //       strokeDashArray: [5, 5]
      //     }),
      //     // Right margin line
      //     new fabric.Line([
      //       appliedLeft + imgWidth * appliedScale, 
      //       0, 
      //       appliedLeft + imgWidth * appliedScale, 
      //       canvas.getHeight()
      //     ], {
      //       stroke: 'red',
      //       strokeWidth: 1,
      //       strokeDashArray: [5, 5]
      //     }),
      //     // Canvas edge indicators
      //     new fabric.Line([0, 0, 0, canvas.getHeight()], {
      //       stroke: 'blue',
      //       strokeWidth: 2
      //     }),
      //     new fabric.Line([canvas.getWidth(), 0, canvas.getWidth(), canvas.getHeight()], {
      //       stroke: 'blue',
      //       strokeWidth: 2
      //     })
      //   ], {
      //     selectable: false,
      //     evented: false,
      //     name: 'debugOverlay'
      //   });
        
      //   canvas.add(debugGroup);
      //   // Move debug overlay to front
      //   canvas.bringObjectToFront(debugGroup);
      // }

      // Force another render to ensure visibility
      canvas.requestRenderAll();

      // Save metadata with adjusted values
      setQuestionMetadata(prev => ({
        ...prev,
        [question.questionId!]: {
          scale: finalScale,
          left: finalLeft,
          top: positioning.top
        }
      }));

      // Add optical choices if needed
      if (isOpticalMode && question.questionProperty) {
       
        const metadata = {
          scale: finalScale,
          left: finalLeft,
          top: finalTop  // Use actual image position instead of positioning.top
        };
        addOpticalChoices(canvas, question, metadata, img);
      } else if (!isOpticalMode) {
        console.log('📊 Not adding optical choices - optical mode is disabled');
      } else if (!question.questionProperty) {
        console.log('📊 Not adding optical choices - no questionProperty data');
      }

      // Restore render setting and render once
      canvas.renderOnAddRemove = previousRenderOnAddRemove;
      canvas.requestRenderAll();
      
      // Final verification (without extra render)
      setTimeout(() => {
        // Check if question image is still there
        const allObjects = canvas.getObjects();
        
        
        
        const questionImg = allObjects.find(obj => 
          (obj as any).name === 'questionBackground'
        );
        
        if (questionImg) {
                  
          // Ensure visibility and disable caching
          questionImg.visible = true;
          questionImg.opacity = 1;
          questionImg.objectCaching = false;
          
          // Clear any cached canvas on the image
          const img = questionImg as unknown as fabric.Image;
          (img as any)._cacheCanvas = null;
          (img as any)._cacheContext = null;
          
          // Ensure proper z-order
          const frame = allObjects.find(obj => 
            (obj as any).name === 'questionFrame'
          );
          
          if (frame) {
            // Make sure frame is behind the question image
            const frameIndex = canvas.getObjects().indexOf(frame);
            const questionIndex = canvas.getObjects().indexOf(questionImg);
            
            if (frameIndex > questionIndex) {
           
              canvas.sendObjectToBack(frame);
              canvas.bringObjectToFront(questionImg);
            }
          }
          
          // Don't render here - already rendered above
        } else {
          console.error('❌ Question image missing from canvas!');
        }
      }, 100);

    } catch (error) {
      console.error('Error loading question background:', error);
    } finally {
      // Reset loading flag after a delay to ensure cleanup
      setTimeout(() => {
        if (currentLoadingQuestionIdRef.current === question.questionId) {
          isLoadingQuestionRef.current = false;
          currentLoadingQuestionIdRef.current = null;
        }
      }, 150);
    }
    
    // For mobile WebView, check if canvas dimensions changed after initial load
    // This handles the case where WebView might resize after content loads
    const isWebViewCheck = window.location.pathname.includes('webview') || window.location.pathname.includes('WebView');
    const isMobileDeviceCheck = window.innerWidth < 768;
    
    if (isWebViewCheck && isMobileDeviceCheck && !isRetry) {
      setTimeout(() => {
        const currentWidth = canvas.getWidth();
        const currentHeight = canvas.getHeight();
        
        if (currentWidth !== initialCanvasWidth || currentHeight !== initialCanvasHeight) {
          console.log('Canvas dimensions changed after question load, reloading:', {
            initial: { width: initialCanvasWidth, height: initialCanvasHeight },
            current: { width: currentWidth, height: currentHeight }
          });
          
          // Clear metadata to force recalculation
          setQuestionMetadata(prev => {
            const newMetadata = { ...prev };
            delete newMetadata[question.questionId || ''];
            return newMetadata;
          });
          
          // Reload question with updated dimensions
          loadQuestionBackground(question, true);
        }
      }, 500); // Check after 500ms
    }
  }, [fabricCanvasRef, isOpticalMode, isMobile, questionMetadata, showAnswerFeedback, 
      opticalAnswers, eliminatedChoices, onOpticalChoiceClick]);


  // Helper function to update optical choice positions based on frame
  const updateOpticalChoicePositionsFrameBased = (canvas: CanvasType, frame: Rect, imageScale: number) => {
    // Get the question image to calculate relative positions
    const questionImg = canvas.getObjects().find(obj => 
      getObjectName(obj) === 'questionBackground' && 
      (obj as any).data?.questionId === (frame as any).data?.questionId
    ) as FabricImage;
    if (!questionImg) return;
    
    // Get the current question from store to access questionProperty
    const currentQuestion = useWhiteboardStore.getState().currentQuestion;
    if (!currentQuestion?.questionProperty) return;
    
    // Get question image position - same approach as addOpticalChoices
    const left = questionImg.left || 0;
    const adjustedTop = questionImg.top || 0; // Already includes parent offset
    
    console.log('🔍 updateOpticalChoicePositionsFrameBased:', {
      left,
      adjustedTop,
      imageScale,
      questionImgWidth: questionImg.width,
      questionImgHeight: questionImg.height,
      questionImgLeft: questionImg.left,
      questionImgTop: questionImg.top
    });
    
    canvas.getObjects().forEach(obj => {
      const objName = getObjectName(obj);
      // Match the actual group name pattern: questionOpticalChoice_{questionId}_{choice}
      if (objName?.startsWith('questionOpticalChoice_') && objName.includes(currentQuestion.questionId || '')) {
        // Update position based on scale
        const originalData = (obj as any).originalPosition;
        if (originalData) {
          // Calculate scale ratio from original
          const scaleRatio = imageScale / (originalData.originalScale || 1);
          
          // originalPosition stores pixel offsets, scale them by the ratio
          const centerX = left + (originalData.offsetX * scaleRatio);
          const centerY = adjustedTop + (originalData.offsetY * scaleRatio);
          
          // Debug log
          const choiceLabel = objName?.split('_').pop();
          console.log(`🎯 Updating optical ${choiceLabel}:`, {
            oldPos: { left: obj.left, top: obj.top },
            newPos: { left: centerX, top: centerY },
            imagePos: { left, adjustedTop },
            originalData,
            scaleRatio
          });
          
          // Set the group position (center) - using scaled offset
          obj.set({
            left: centerX,
            top: centerY
          });
          
          // Update bounding box for stroke detection
          obj.setCoords();
          
          // For groups, update the inner objects
          if (obj.type === 'group') {
            const group = obj as Group;
            const items = group.getObjects();
            if (items && items.length >= 2) {
              const bgCircle = items[0] as Ellipse;
              
              // Calculate scaled dimensions with expansion factor
              const scaledWidth = Math.max(originalData.baseWidth * scaleRatio * (isMobile ? 1.2 : 1.4), isMobile ? 15 : 20);
              const scaledHeight = Math.max(originalData.baseHeight * scaleRatio * (isMobile ? 1.1 : 1.2), isMobile ? 15 : 20);
              
              // Update ellipse radii
              bgCircle.set({
                rx: Math.max(scaledWidth / 2, isMobile ? 7 : 10),
                ry: Math.max(scaledHeight / 2, isMobile ? 7 : 10)
              });
              
              // Update coordinates for all group items
              group.getObjects().forEach(item => item.setCoords());
            }
          }
        }
      }
    });
  };


  const addOpticalChoices = (
    canvas: CanvasType,
    question: QBTestQuestionEto,
    metadata: { scale: number; left: number; top: number },
    img: FabricImage
  ) => {
    if (!question.questionProperty) {
      console.log('❌ questionProperty boş, optik seçimler eklenemedi');
      return;
    }
    
    try {
      const questionProperty = typeof question.questionProperty === 'string' 
        ? JSON.parse(question.questionProperty) 
        : question.questionProperty;
      
     
      
      if (!questionProperty.choicePos || !questionProperty.imageWidth || !questionProperty.imageHeight) {
        console.log('❌ No choice positions found in questionProperty:', {
          hasChoicePos: !!questionProperty.choicePos,
          hasImageWidth: !!questionProperty.imageWidth,
          hasImageHeight: !!questionProperty.imageHeight
        });
        return;
      }
      
      
      
      // metadata.top is already the question image's final position
      // It already includes parent offset if parent exists
      const adjustedTop = metadata.top;
      const left = metadata.left;
      const scale = metadata.scale;
      
      // Calculate scale factors - using simple scale as in working version
      const scaleFactorX = scale;
      const scaleFactorY = scale;
      
      
     
      
      Object.entries(questionProperty.choicePos).forEach(([choiceLabel, coords]: [string, any]) => {
        if (!Array.isArray(coords) || coords.length !== 4) return;
        
        const [x1, y1, x2, y2] = coords as number[];
        
        // Scale and position the choice area - exact same as old implementation
        const originalX = left + (x1 * scaleFactorX);
        const originalY = adjustedTop + (y1 * scaleFactorY);
        const originalWidth = (x2 - x1) * scaleFactorX;
        const originalHeight = (y2 - y1) * scaleFactorY;
        
      
        
        // Validate dimensions to prevent zero width/height
        if (originalWidth <= 0 || originalHeight <= 0) {
          console.warn(`Invalid optical choice dimensions for ${choiceLabel}:`, { originalWidth, originalHeight });
          return;
        }
        
        // Expand the hit area for easier clicking - smaller on mobile
        const scaledWidth = Math.max(originalWidth * (isMobile ? 1.2 : 1.4), isMobile ? 15 : 20); // Smaller minimum size on mobile
        const scaledHeight = Math.max(originalHeight * (isMobile ? 1.1 : 1.2), isMobile ? 15 : 20); // Smaller minimum size on mobile
        
        // Calculate position with size increase adjustment - center the expanded area
        const widthIncrease = scaledWidth - originalWidth;
        const heightIncrease = scaledHeight - originalHeight;
        const scaledX = originalX - (widthIncrease * 0.5);  // Center horizontally
        const scaledY = originalY - (heightIncrease * 0.5); // Center vertically
      
        // Create invisible hit area for selection - ellipse shape (matching old implementation)
        const bgCircle = new Ellipse({
          name: `opticalChoice_${choiceLabel}`,  // Old naming pattern
          left: scaledX + scaledWidth / 2,
          top: scaledY + scaledHeight / 2,
          rx: Math.max(scaledWidth / 2, isMobile ? 7 : 10), // Smaller minimum radius on mobile
          ry: Math.max(scaledHeight / 2, isMobile ? 7 : 10), // Smaller minimum radius on mobile
          originX: 'center',
          originY: 'center',
          fill: 'transparent',
          stroke: 'transparent',
          strokeWidth: 0,
          selectable: false,
          evented: true,  // Make sure individual element is evented
          hasControls: false,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          erasable: false as any,
          cursor: 'pointer',
          perPixelTargetFind: true  // Enable precise hit detection
        });
        
        // Create feedback icon
        const feedbackIcon = new FabricText('', {
          left: scaledX + scaledWidth / 2,
          top: scaledY + scaledHeight / 2,
          fontSize: Math.max(Math.min(scaledWidth, scaledHeight) * 0.6, 12), // Ensure minimum font size
          fill: 'transparent',
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: false,
          erasable: false
        });
        
        // Apply current state if already selected
        const currentAnswer = opticalAnswers?.[question.questionId || ''];
        if (currentAnswer === choiceLabel) {
          bgCircle.set({
            fill: 'rgba(251, 146, 60, 0.3)',
            stroke: '#fb923c',
            strokeWidth: 2
          });
        }
        
        // Add click handler to bgCircle
        bgCircle.on('mousedown', (e: any) => {
       
          if (e.e) {
            e.e.preventDefault();
            e.e.stopPropagation();
          }
          
          if (onOpticalChoiceClick) {
            onOpticalChoiceClick(choiceLabel, question.questionId!);
          }
        });
        
        // Validate objects before creating group
        if (!bgCircle || !feedbackIcon) {
          console.error('Failed to create optical choice elements');
          return;
        }
        
        // Ensure ellipse has valid dimensions
        if (bgCircle.rx! <= 0 || bgCircle.ry! <= 0) {
          console.warn('Invalid ellipse dimensions, skipping optical choice:', choiceLabel);
          return;
        }
        
        // Create group with choice label for reference (like old whiteboard)
        const group = new Group([bgCircle, feedbackIcon], {
          originX: 'center',
          originY: 'center',
          selectable: false,
          evented: true,  // Allow events for circle detection
          hasControls: false,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          erasable: false,
          perPixelTargetFind: true,  // Enable precise hit detection
          subTargetCheck: true  // Check sub-objects for events
        } as any);
        
        // Set name and metadata after creation (Fabric.js 6 pattern)
        group.set('name', `questionOpticalChoice_${question.questionId}_${choiceLabel}`);
        (group as any).choiceLabel = choiceLabel;
        (group as any).questionId = question.questionId;
        
        // Store original position for resize handling
        // Store the center position offset relative to the image position
        // Store pixel offsets without normalization
        const storedOffsetX = (scaledX + scaledWidth / 2 - left);
        const storedOffsetY = (scaledY + scaledHeight / 2 - adjustedTop);
        
       
        
        (group as any).originalPosition = {
          offsetX: storedOffsetX,
          offsetY: storedOffsetY,
          baseWidth: originalWidth,  // Store base dimensions
          baseHeight: originalHeight,
          originalScale: scale // Store the scale used during creation
        };
        
        // Add click handler to the group as well
        group.on('mousedown', (e: any) => {
          console.log(`🕹️ Grup tıklama: Şık ${choiceLabel}`);
          if (e.e) {
            e.e.preventDefault();
            e.e.stopPropagation();
          }
          
          if (onOpticalChoiceClick) {
            onOpticalChoiceClick(choiceLabel, question.questionId!);
          }
        });
        
        // Ensure group coordinates are set correctly
        group.setCoords();
        canvas.add(group);
        
        // Use z-order utility to place in correct layer
        placeObjectInCorrectLayer(canvas, group);
      });
    
    // Update choices based on current selection
    if (question.questionId) {
      const currentAnswer = opticalAnswers[question.questionId];
      console.log('🎯 Updating optical choices after load:', {
        questionId: question.questionId,
        currentAnswer,
        allAnswers: opticalAnswers
      });
      updateOpticalChoices(question.questionId, currentAnswer);
      
      // Force a render to ensure visual update
      setTimeout(() => {
        if (question.questionId) {
          updateOpticalChoices(question.questionId, currentAnswer);
        }
        canvas.requestRenderAll();
      }, 100);
    }
    
    // Verify optical choices were added
    const opticalCount = canvas.getObjects().filter(obj => {
      const name = getObjectName(obj);
      return name?.startsWith('questionOpticalChoice_');
    }).length;
    
    console.log(`✅ Toplam ${opticalCount} optik seçim eklendi`);
    
    // Don't render here - will be rendered by the caller
    } catch (error) {
      console.error('Error adding optical choices:', error);
    }
  };

  const clearQuestionBackground = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    // Don't clear if currently loading a question
    if (isLoadingQuestionRef.current) {
      console.log('⚠️ Question is currently loading, skipping clear');
      return;
    }

    // Get current question ID before clearing
    const currentQuestionId = useWhiteboardStore.getState().currentQuestionId;
    
    // Create a copy to avoid modification during iteration
    const allObjects = [...canvas.getObjects()];
    const questionElements = allObjects.filter(obj => {
      const name = getObjectName(obj);
      // Also check for any image objects that might be question backgrounds
      const isQuestionImage = obj.type === 'image' && (
        name === 'questionBackground' || 
        name === 'parentQuestionBackground'
      );
      
      return isQuestionImage ||
             name === 'questionFrame' ||
             name === 'questionResizeHandle' ||
             name === 'questionMoveHandle' ||
             name === 'questionMoveIcon' ||
             name?.startsWith('opticalChoice_') ||
             name?.startsWith('opticalChoiceFeedback_') ||
             name?.startsWith('opticalQuestionNumber_') ||
             name?.startsWith('questionOpticalChoice_') ||
             name?.startsWith('opticalSelectionCircle_');
    });
    
    // Remove each element
    questionElements.forEach(obj => {
      canvas.remove(obj);
    });
    
    // Remove all overlay strokes for this question
    if (currentQuestionId) {
      removeQuestionStrokes(canvas, currentQuestionId);
    }
    
    // Force a render to ensure cleanup is visible
    canvas.requestRenderAll();
  }, [fabricCanvasRef]);

  const updateOpticalChoices = useCallback((questionId: string, selectedChoice: string | null) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || !isOpticalMode) return;
    
    // Get eliminated choices for this question
    const eliminated = eliminatedChoices[questionId] || '';


    // Update all optical choices for this question
    canvas.getObjects().forEach(obj => {
      const objName = getObjectName(obj);
      
      // Handle questionOpticalChoice groups
      if (objName?.startsWith('questionOpticalChoice_')) {
        // Extract question ID and choice label from name: questionOpticalChoice_{questionId}_{choice}
        const parts = objName.split('_');
        if (parts.length >= 3) {
          const objQuestionId = parts[1];
          const choiceLabel = parts[2];
          
          // Only update choices for the current question
          if (objQuestionId === questionId && obj.type === 'group') {
            const group = obj as Group;
            const items = group.getObjects();
            
            if (items && items.length >= 2) {
              const bgCircle = items[0] as Ellipse;
              const feedbackIcon = items[1] as FabricText;
              
              
              // Check if this choice is eliminated
              const isEliminated = eliminated.includes(choiceLabel);
              
              if (selectedChoice === choiceLabel) {
                // Selected state - orange fill and border
                bgCircle.set({
                  fill: 'rgba(251, 146, 60, 0.3)',
                  stroke: '#fb923c',
                  strokeWidth: 2
                });
                
                // Update feedback icon if exists
                if (feedbackIcon && showAnswerFeedback) {
                  // This would be handled by the answer feedback system
                }
              } else if (isEliminated) {
                // Eliminated state - no visual changes, keep transparent
                bgCircle.set({
                  fill: 'transparent',
                  stroke: 'transparent',
                  strokeWidth: 0
                });
                
                // Clear feedback icon
                if (feedbackIcon) {
                  feedbackIcon.set({
                    text: '',
                    fill: 'transparent'
                  });
                }
              } else {
                // Unselected state - transparent
                bgCircle.set({
                  fill: 'transparent',
                  stroke: 'transparent',
                  strokeWidth: 0
                });
                
                // Clear feedback icon
                if (feedbackIcon) {
                  feedbackIcon.set({
                    text: '',
                    fill: 'transparent'
                  });
                }
                
                // Don't remove strokes - keep elimination marks visible
              }
            }
          }
        }
      }
    });
    
    // Don't remove strokes when deselecting - keep elimination marks visible
    
    // Batch render with requestAnimationFrame
    requestAnimationFrame(() => {
      canvas.requestRenderAll();
    });
  }, [fabricCanvasRef, isOpticalMode, showAnswerFeedback]);

  // Update handles visibility based on current tool
  const updateHandlesVisibility = useCallback(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas || isMobile) return;
    
    const { currentTool } = useWhiteboardStore.getState();
    const isSelectMode = currentTool === 'select';
    
    canvas.getObjects().forEach(obj => {
      const objName = getObjectName(obj);
      
      // Update question images to be selectable/movable in select mode
      if (objName === 'questionBackground' || objName === 'parentQuestionBackground') {
        obj.set({
          selectable: isSelectMode && objName === 'questionBackground', // Only main image is selectable
          evented: isSelectMode && objName === 'questionBackground',
          hasControls: isSelectMode && objName === 'questionBackground',
          hasBorders: isSelectMode && objName === 'questionBackground',
          lockMovementX: !isSelectMode,
          lockMovementY: !isSelectMode,
          lockScalingX: !isSelectMode,
          lockScalingY: !isSelectMode
        });
      }
      
      // Remove old resize/move handles if they exist
      if (objName === 'questionResizeHandle' || objName === 'questionMoveHandle') {
        canvas.remove(obj);
      }
    });
    
    canvas.requestRenderAll();
  }, [fabricCanvasRef, isMobile]);

  return {
    loadQuestionBackground,
    clearQuestionBackground,
    updateOpticalChoices,
    updateHandlesVisibility,
    questionMetadata,
    setQuestionMetadata,
    updateOpticalChoicePositionsFrameBased,
    isLoadingQuestion: isLoadingQuestionRef.current
  };
};